/**
 * Theme Builder - Dear Diary Customization
 * 
 * Allows users to fully customize their Dear Diary theme with:
 * - Paper backgrounds (textures, colors, patterns)
 * - Fonts (handwritten, body, UI)
 * - Colors (ink, links, accents, highlights)
 * - Interactive states (hover, click, focus)
 * - Dark mode variants
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Palette, Type, Image, Sun, Moon, 
  Save, RotateCcw, Eye, ChevronLeft,
  Droplets, MousePointer, Cloud, CloudOff
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { useTheme, DIARY_FONTS } from '../components/providers/ThemeProvider'
import { useDiaryThemeSync } from '../hooks/useDiaryThemeSync'
import { ThemeStudio } from '../components/theme/ThemeStudio'

// ═══════════════════════════════════════════════════════════════════════════
// Theme Data Types
// ═══════════════════════════════════════════════════════════════════════════

export interface DiaryThemeCustomization {
  id: string
  name: string
  description: string
  createdAt: string
  updatedAt: string

  paper: {
    type: 'solid' | 'gradient' | 'texture'
    solidColor: string
    gradientFrom: string
    gradientTo: string
    gradientDirection: string
    textureId: string
    patternId: string
    patternOpacity: number
  }

  fonts: {
    handwritten: string
    body: string
    ui: string
  }

  light: {
    paperPrimary: string
    paperSecondary: string
    paperMargin: string
    inkPrimary: string
    inkSecondary: string
    inkFaded: string
    accentPrimary: string
    accentSecondary: string
    bookmark: string
    highlight: string
    linkDefault: string
    linkHover: string
    linkVisited: string
    hoverBg: string
    activeBg: string
    focusRing: string
    borderLight: string
    borderMedium: string
    shadowColor: string
  }

  dark: {
    paperPrimary: string
    paperSecondary: string
    paperMargin: string
    inkPrimary: string
    inkSecondary: string
    inkFaded: string
    accentPrimary: string
    accentSecondary: string
    bookmark: string
    highlight: string
    linkDefault: string
    linkHover: string
    linkVisited: string
    hoverBg: string
    activeBg: string
    focusRing: string
    borderLight: string
    borderMedium: string
    shadowColor: string
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Paper Textures - Realistic paper backgrounds
// ═══════════════════════════════════════════════════════════════════════════

interface PaperTexture {
  id: string
  name: string
  description: string
  // CSS for the texture effect
  getStyle: (baseColor: string, inkColor: string) => React.CSSProperties
}

const paperTextures: PaperTexture[] = [
  {
    id: 'plain',
    name: 'Plain',
    description: 'Clean solid color',
    getStyle: (baseColor) => ({
      backgroundColor: baseColor,
    }),
  },
  {
    id: 'aged',
    name: 'Aged Paper',
    description: 'Subtle vintage aging effect',
    getStyle: (baseColor) => ({
      backgroundColor: baseColor,
      backgroundImage: `
        radial-gradient(ellipse at 20% 30%, rgba(139, 115, 85, 0.08) 0%, transparent 50%),
        radial-gradient(ellipse at 80% 70%, rgba(139, 115, 85, 0.06) 0%, transparent 40%),
        radial-gradient(ellipse at 50% 50%, rgba(92, 64, 51, 0.03) 0%, transparent 70%)
      `,
    }),
  },
  {
    id: 'parchment',
    name: 'Parchment',
    description: 'Classic parchment texture',
    getStyle: (baseColor) => ({
      backgroundColor: baseColor,
      backgroundImage: `
        url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E"),
        radial-gradient(ellipse at 0% 0%, rgba(139, 115, 85, 0.1) 0%, transparent 50%),
        radial-gradient(ellipse at 100% 100%, rgba(139, 115, 85, 0.08) 0%, transparent 50%)
      `,
    }),
  },
  {
    id: 'kraft',
    name: 'Kraft Paper',
    description: 'Brown recycled paper look',
    getStyle: (baseColor) => ({
      backgroundColor: baseColor,
      backgroundImage: `
        url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.06'/%3E%3C/svg%3E")
      `,
    }),
  },
  {
    id: 'watercolor',
    name: 'Watercolor',
    description: 'Soft watercolor wash',
    getStyle: (baseColor) => ({
      backgroundColor: baseColor,
      backgroundImage: `
        radial-gradient(ellipse at 10% 20%, rgba(123, 104, 166, 0.08) 0%, transparent 40%),
        radial-gradient(ellipse at 90% 80%, rgba(74, 144, 164, 0.06) 0%, transparent 40%),
        radial-gradient(ellipse at 50% 50%, rgba(196, 92, 74, 0.04) 0%, transparent 60%)
      `,
    }),
  },
  {
    id: 'linen',
    name: 'Linen',
    description: 'Fabric-like texture',
    getStyle: (baseColor) => ({
      backgroundColor: baseColor,
      backgroundImage: `
        repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 4px),
        repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 4px)
      `,
    }),
  },
]

// ═══════════════════════════════════════════════════════════════════════════
// Line Patterns - Notebook-style ruling
// ═══════════════════════════════════════════════════════════════════════════

interface LinePattern {
  id: string
  name: string
  description: string
  getStyle: (inkColor: string, opacity: number) => React.CSSProperties
}

const linePatterns: LinePattern[] = [
  {
    id: 'none',
    name: 'No Lines',
    description: 'Plain paper',
    getStyle: () => ({}),
  },
  {
    id: 'ruled',
    name: 'College Ruled',
    description: 'Classic lined paper',
    getStyle: (inkColor, opacity) => ({
      backgroundImage: `repeating-linear-gradient(
        transparent,
        transparent 27px,
        ${inkColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 27px,
        ${inkColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 28px
      )`,
      backgroundSize: '100% 28px',
    }),
  },
  {
    id: 'wide-ruled',
    name: 'Wide Ruled',
    description: 'Wider line spacing',
    getStyle: (inkColor, opacity) => ({
      backgroundImage: `repeating-linear-gradient(
        transparent,
        transparent 35px,
        ${inkColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 35px,
        ${inkColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 36px
      )`,
      backgroundSize: '100% 36px',
    }),
  },
  {
    id: 'narrow-ruled',
    name: 'Narrow Ruled',
    description: 'Tighter line spacing',
    getStyle: (inkColor, opacity) => ({
      backgroundImage: `repeating-linear-gradient(
        transparent,
        transparent 19px,
        ${inkColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 19px,
        ${inkColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 20px
      )`,
      backgroundSize: '100% 20px',
    }),
  },
  {
    id: 'margin',
    name: 'With Red Margin',
    description: 'Ruled with margin line',
    getStyle: (inkColor, opacity) => ({
      backgroundImage: `
        linear-gradient(90deg, transparent 59px, #C45C4A40 59px, #C45C4A40 61px, transparent 61px),
        repeating-linear-gradient(
          transparent,
          transparent 27px,
          ${inkColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 27px,
          ${inkColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 28px
        )
      `,
      backgroundSize: '100% 28px',
    }),
  },
  {
    id: 'dot-grid',
    name: 'Dot Grid',
    description: 'Bullet journal style',
    getStyle: (inkColor, opacity) => ({
      backgroundImage: `radial-gradient(circle, ${inkColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 1px, transparent 1px)`,
      backgroundSize: '20px 20px',
    }),
  },
  {
    id: 'graph',
    name: 'Graph Paper',
    description: 'Square grid',
    getStyle: (inkColor, opacity) => ({
      backgroundImage: `
        linear-gradient(${inkColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 1px, transparent 1px),
        linear-gradient(90deg, ${inkColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')} 1px, transparent 1px)
      `,
      backgroundSize: '20px 20px',
    }),
  },
  {
    id: 'isometric',
    name: 'Isometric',
    description: '3D drawing grid',
    getStyle: (inkColor, opacity) => ({
      backgroundImage: `
        linear-gradient(30deg, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')} 12%, transparent 12.5%, transparent 87%, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')} 87.5%, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')}),
        linear-gradient(150deg, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')} 12%, transparent 12.5%, transparent 87%, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')} 87.5%, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')}),
        linear-gradient(30deg, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')} 12%, transparent 12.5%, transparent 87%, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')} 87.5%, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')}),
        linear-gradient(150deg, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')} 12%, transparent 12.5%, transparent 87%, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')} 87.5%, ${inkColor}${Math.round(opacity * 255 * 0.5).toString(16).padStart(2, '0')})
      `,
      backgroundSize: '40px 70px',
      backgroundPosition: '0 0, 0 0, 20px 35px, 20px 35px',
    }),
  },
]

// ═══════════════════════════════════════════════════════════════════════════
// Default Theme
// ═══════════════════════════════════════════════════════════════════════════

const defaultTheme: DiaryThemeCustomization = {
  id: 'default',
  name: 'Classic Journal',
  description: 'Warm cream paper with sepia ink',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),

  paper: {
    type: 'solid',
    solidColor: '#FDF8F3',
    gradientFrom: '#FDF8F3',
    gradientTo: '#F5EDE4',
    gradientDirection: '180deg',
    textureId: 'aged',
    patternId: 'none',
    patternOpacity: 0.15,
  },

  fonts: {
    handwritten: "'Kalam', cursive",
    body: "'Lora', serif",
    ui: "'Nunito', sans-serif",
  },

  light: {
    paperPrimary: '#FDF8F3',
    paperSecondary: '#F5EDE4',
    paperMargin: '#E8E0D5',
    inkPrimary: '#2D2A26',
    inkSecondary: '#5C4033',
    inkFaded: '#8B7355',
    accentPrimary: '#7851A9',
    accentSecondary: '#4A90A4',
    bookmark: '#C45C4A',
    highlight: '#FFF3CD',
    linkDefault: '#1E3A5F',
    linkHover: '#2D5A8F',
    linkVisited: '#5C4033',
    hoverBg: 'rgba(232, 224, 213, 0.5)',
    activeBg: 'rgba(196, 92, 74, 0.1)',
    focusRing: '#7851A9',
    borderLight: '#E8E0D5',
    borderMedium: '#D4C8B8',
    shadowColor: 'rgba(92, 64, 51, 0.1)',
  },

  dark: {
    paperPrimary: '#2A2520',
    paperSecondary: '#332D27',
    paperMargin: '#4A433C',
    inkPrimary: '#E8E0D5',
    inkSecondary: '#C4A882',
    inkFaded: '#A69280',
    accentPrimary: '#A589DC',
    accentSecondary: '#6AB0C4',
    bookmark: '#D47A6A',
    highlight: '#4A4230',
    linkDefault: '#8FB8D4',
    linkHover: '#A8D0E8',
    linkVisited: '#C4A882',
    hoverBg: 'rgba(74, 67, 60, 0.5)',
    activeBg: 'rgba(212, 122, 106, 0.15)',
    focusRing: '#A589DC',
    borderLight: '#4A433C',
    borderMedium: '#5A534C',
    shadowColor: 'rgba(0, 0, 0, 0.3)',
  },
}

// ═══════════════════════════════════════════════════════════════════════════
// Preset Themes
// ═══════════════════════════════════════════════════════════════════════════

const presetThemes: DiaryThemeCustomization[] = [
  defaultTheme,
  {
    ...defaultTheme,
    id: 'notebook',
    name: 'School Notebook',
    description: 'College ruled with margin',
    paper: { ...defaultTheme.paper, textureId: 'plain', patternId: 'margin', patternOpacity: 0.2 },
  },
  {
    ...defaultTheme,
    id: 'aged-parchment',
    name: 'Aged Parchment',
    description: 'Weathered paper texture',
    paper: { ...defaultTheme.paper, solidColor: '#F0E6D2', textureId: 'parchment' },
    light: { ...defaultTheme.light, paperPrimary: '#F0E6D2', inkPrimary: '#3D2B1F', inkSecondary: '#5D4037' },
  },
  {
    ...defaultTheme,
    id: 'bullet-journal',
    name: 'Bullet Journal',
    description: 'Dot grid for planning',
    paper: { ...defaultTheme.paper, textureId: 'plain', patternId: 'dot-grid', patternOpacity: 0.25 },
  },
  {
    ...defaultTheme,
    id: 'engineer',
    name: 'Engineer\'s Pad',
    description: 'Graph paper for sketches',
    paper: { ...defaultTheme.paper, solidColor: '#FAFAF5', textureId: 'plain', patternId: 'graph', patternOpacity: 0.15 },
    light: { ...defaultTheme.light, paperPrimary: '#FAFAF5', inkPrimary: '#1A365D', inkSecondary: '#2C5282' },
  },
  {
    ...defaultTheme,
    id: 'rose-journal',
    name: 'Rose Journal',
    description: 'Soft pink with watercolor',
    paper: { ...defaultTheme.paper, solidColor: '#FFF5F5', textureId: 'watercolor' },
    light: { 
      ...defaultTheme.light, 
      paperPrimary: '#FFF5F5', 
      paperSecondary: '#FED7D7',
      bookmark: '#9B2C2C',
      accentPrimary: '#B83280',
    },
  },
  // NEW PRESETS FOR PHASE 4
  {
    ...defaultTheme,
    id: 'cozy-autumn',
    name: 'Cozy Autumn',
    description: 'Warm fall colors with kraft paper',
    paper: { ...defaultTheme.paper, solidColor: '#F5EBE0', textureId: 'kraft' },
    light: { 
      ...defaultTheme.light, 
      paperPrimary: '#F5EBE0', 
      paperSecondary: '#E8D5C4',
      inkPrimary: '#5D4037',
      inkSecondary: '#795548',
      bookmark: '#D84315',
      accentPrimary: '#BF360C',
      accentSecondary: '#F4511E',
      highlight: '#FFE0B2',
    },
  },
  {
    ...defaultTheme,
    id: 'ocean-calm',
    name: 'Ocean Calm',
    description: 'Serene blue tones',
    paper: { ...defaultTheme.paper, solidColor: '#F0F7FF', textureId: 'watercolor' },
    light: { 
      ...defaultTheme.light, 
      paperPrimary: '#F0F7FF', 
      paperSecondary: '#E3F2FD',
      inkPrimary: '#1565C0',
      inkSecondary: '#42A5F5',
      bookmark: '#0277BD',
      accentPrimary: '#0288D1',
      accentSecondary: '#4FC3F7',
      highlight: '#B3E5FC',
    },
    fonts: { handwritten: "'Caveat', cursive", body: "'Lora', serif", ui: "'Nunito', sans-serif" },
  },
  {
    ...defaultTheme,
    id: 'forest-retreat',
    name: 'Forest Retreat',
    description: 'Natural green tones',
    paper: { ...defaultTheme.paper, solidColor: '#F1F8E9', textureId: 'linen' },
    light: { 
      ...defaultTheme.light, 
      paperPrimary: '#F1F8E9', 
      paperSecondary: '#DCEDC8',
      inkPrimary: '#33691E',
      inkSecondary: '#558B2F',
      bookmark: '#827717',
      accentPrimary: '#689F38',
      accentSecondary: '#8BC34A',
      highlight: '#F0F4C3',
    },
  },
  {
    ...defaultTheme,
    id: 'midnight-sepia',
    name: 'Midnight Sepia',
    description: 'Dark mode with warm tones',
    paper: { ...defaultTheme.paper, solidColor: '#2D2520', textureId: 'aged' },
    light: { 
      ...defaultTheme.light, 
      paperPrimary: '#2D2520', 
      paperSecondary: '#3D3530',
      paperMargin: '#4A433C',
      inkPrimary: '#E8E0D5',
      inkSecondary: '#C4A882',
      inkFaded: '#A69280',
      bookmark: '#D47A6A',
      accentPrimary: '#A589DC',
      accentSecondary: '#BB9B7C',
      highlight: '#4A4230',
    },
  },
  {
    ...defaultTheme,
    id: 'lavender-dreams',
    name: 'Lavender Dreams',
    description: 'Soft purple tones',
    paper: { ...defaultTheme.paper, solidColor: '#F5F3FF', textureId: 'watercolor' },
    light: { 
      ...defaultTheme.light, 
      paperPrimary: '#F5F3FF', 
      paperSecondary: '#EDE9FE',
      inkPrimary: '#5B21B6',
      inkSecondary: '#7C3AED',
      bookmark: '#9333EA',
      accentPrimary: '#8B5CF6',
      accentSecondary: '#A78BFA',
      highlight: '#DDD6FE',
    },
    fonts: { handwritten: "'Dancing Script', cursive", body: "'Lora', serif", ui: "'Nunito', sans-serif" },
  },
  {
    ...defaultTheme,
    id: 'minimalist',
    name: 'Minimalist',
    description: 'Clean and simple',
    paper: { ...defaultTheme.paper, solidColor: '#FFFFFF', textureId: 'plain', patternId: 'none' },
    light: { 
      ...defaultTheme.light, 
      paperPrimary: '#FFFFFF', 
      paperSecondary: '#F8F8F8',
      inkPrimary: '#1A1A1A',
      inkSecondary: '#4A4A4A',
      bookmark: '#FF4444',
      accentPrimary: '#000000',
      accentSecondary: '#666666',
    },
    fonts: { handwritten: "'Patrick Hand', cursive", body: "'Lora', serif", ui: "'Inter', sans-serif" },
  },
  {
    ...defaultTheme,
    id: 'vintage-typewriter',
    name: 'Vintage Typewriter',
    description: 'Classic typewritten look',
    paper: { ...defaultTheme.paper, solidColor: '#FFFEF5', textureId: 'aged', patternId: 'none' },
    light: { 
      ...defaultTheme.light, 
      paperPrimary: '#FFFEF5', 
      paperSecondary: '#F5F4E8',
      inkPrimary: '#1C1C1C',
      inkSecondary: '#3D3D3D',
      bookmark: '#8B0000',
      accentPrimary: '#2F4F4F',
    },
    fonts: { handwritten: "'Courier New', monospace", body: "'Courier New', monospace", ui: "'Nunito', sans-serif" },
  },
]

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

export const ThemeBuilder: React.FC = () => {
  const navigate = useNavigate()
  const { isDiary, setThemeStyle } = useTheme()
  const { saveDiaryTheme } = useDiaryThemeSync()
  
  const [theme, setTheme] = useState<DiaryThemeCustomization>(() => {
    const saved = localStorage.getItem('horton-active-diary-theme')
    return saved ? JSON.parse(saved) : defaultTheme
  })
  const [layer, setLayer] = useState<'studio' | 'journal'>('studio')
  const [activeSection, setActiveSection] = useState<'presets' | 'paper' | 'fonts' | 'colors' | 'interactions'>('presets')
  const [previewMode, setPreviewMode] = useState<'light' | 'dark'>('light')
  const [hasChanges, setHasChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'synced' | 'error'>('idle')

  // Journal extras still need the diary theme so paper tokens exist, but
  // the studio layer works on any theme and must not yank the user into
  // Journal the moment they open this page.
  useEffect(() => {
    if (layer === 'journal' && !isDiary) {
      setThemeStyle('diary')
    }
  }, [layer, isDiary, setThemeStyle])

  // Get combined paper background style
  const getPaperBackground = useCallback((t: DiaryThemeCustomization, mode: 'light' | 'dark'): React.CSSProperties => {
    const colors = t[mode]
    const baseColor = t.paper.type === 'gradient' 
      ? colors.paperPrimary // Use primary for gradient base
      : t.paper.solidColor || colors.paperPrimary
    
    // Get texture style
    const texture = paperTextures.find(tex => tex.id === t.paper.textureId) || paperTextures[0]
    const textureStyle = texture.getStyle(baseColor, colors.inkFaded)
    
    // Get pattern style
    const pattern = linePatterns.find(pat => pat.id === t.paper.patternId) || linePatterns[0]
    const patternStyle = pattern.getStyle(colors.inkFaded, t.paper.patternOpacity)
    
    // Combine backgrounds
    const backgrounds: string[] = []
    
    if (patternStyle.backgroundImage) {
      backgrounds.push(patternStyle.backgroundImage as string)
    }
    if (textureStyle.backgroundImage) {
      backgrounds.push(textureStyle.backgroundImage as string)
    }
    
    // Handle gradient type
    if (t.paper.type === 'gradient') {
      backgrounds.push(`linear-gradient(${t.paper.gradientDirection}, ${t.paper.gradientFrom}, ${t.paper.gradientTo})`)
    }
    
    return {
      backgroundColor: baseColor,
      backgroundImage: backgrounds.length > 0 ? backgrounds.join(', ') : undefined,
      backgroundSize: [patternStyle.backgroundSize, textureStyle.backgroundSize].filter(Boolean).join(', ') || undefined,
      backgroundPosition: patternStyle.backgroundPosition as string || undefined,
    }
  }, [])

  // Apply theme to CSS variables
  const applyThemeToDocument = useCallback((t: DiaryThemeCustomization) => {
    const root = document.documentElement
    const colors = previewMode === 'light' ? t.light : t.dark

    root.style.setProperty('--paper-cream', colors.paperPrimary)
    root.style.setProperty('--paper-aged', colors.paperSecondary)
    root.style.setProperty('--paper-margin', colors.paperMargin)
    root.style.setProperty('--ink-black', colors.inkPrimary)
    root.style.setProperty('--ink-sepia', colors.inkSecondary)
    root.style.setProperty('--ink-faded', colors.inkFaded)
    root.style.setProperty('--horton-purple', colors.accentPrimary)
    root.style.setProperty('--horton-blue', colors.accentSecondary)
    root.style.setProperty('--bookmark-red', colors.bookmark)
    root.style.setProperty('--highlight-yellow', colors.highlight)
    // Fonts are deliberately NOT written here: the font vars belong to
    // ThemeProvider's role effects (the "Make it yours" picks). Writing
    // them from the builder clobbered the whole app's fonts just by
    // VISITING this page — and it stuck after navigating away. The
    // preview pane renders fonts via inline style={{ fontFamily }}.
  }, [previewMode])

  useEffect(() => {
    applyThemeToDocument(theme)
  }, [theme, applyThemeToDocument])

  // The color vars this page live-previews leak the same way the fonts
  // did: inline on <html>, surviving navigation. Snapshot every var we
  // touch on mount and restore on unmount, handing ownership back to
  // ThemeProvider the moment the builder closes.
  useEffect(() => {
    const root = document.documentElement
    const PROPS = [
      '--paper-cream', '--paper-aged', '--paper-margin',
      '--ink-black', '--ink-sepia', '--ink-faded',
      '--horton-purple', '--horton-blue', '--bookmark-red', '--highlight-yellow',
    ]
    const prev = PROPS.map(pr => [pr, root.style.getPropertyValue(pr)] as const)
    return () => {
      prev.forEach(([pr, v]) => {
        if (v) root.style.setProperty(pr, v)
        else root.style.removeProperty(pr)
      })
    }
  }, [])

  // Update theme value
  const updateTheme = <K extends keyof DiaryThemeCustomization>(
    section: K,
    value: DiaryThemeCustomization[K]
  ) => {
    setTheme(prev => ({
      ...prev,
      [section]: value,
      updatedAt: new Date().toISOString(),
    }))
    setHasChanges(true)
  }

  // Update nested color value
  const updateColor = (mode: 'light' | 'dark', key: string, value: string) => {
    setTheme(prev => ({
      ...prev,
      [mode]: { ...prev[mode], [key]: value },
      updatedAt: new Date().toISOString(),
    }))
    setHasChanges(true)
  }

  // Save theme to localStorage AND server
  const saveTheme = async () => {
    setIsSaving(true)
    setSyncStatus('syncing')
    
    try {
      // Save to custom themes list
      const customThemes = JSON.parse(localStorage.getItem('horton-custom-themes') || '[]')
      const existingIndex = customThemes.findIndex((t: DiaryThemeCustomization) => t.id === theme.id)
      
      if (existingIndex >= 0) {
        customThemes[existingIndex] = theme
      } else {
        customThemes.push({ ...theme, id: `custom-${Date.now()}` })
      }
      
      localStorage.setItem('horton-custom-themes', JSON.stringify(customThemes))
      
      // Save active theme to localStorage AND server
      const result = await saveDiaryTheme(theme)
      
      setHasChanges(false)
      
      if (result.success) {
        setSyncStatus('synced')
        // Show success briefly then reset
        setTimeout(() => setSyncStatus('idle'), 2000)
      } else {
        setSyncStatus('error')
        console.warn('Theme saved locally, server sync issue:', result.error)
      }
    } catch (error) {
      console.error('Error saving theme:', error)
      setSyncStatus('error')
    } finally {
      setIsSaving(false)
    }
  }

  // Reset to default
  const resetTheme = () => {
    if (confirm('Reset all customizations to default?')) {
      setTheme(defaultTheme)
      setHasChanges(false)
    }
  }

  // Load preset
  const loadPreset = (preset: DiaryThemeCustomization) => {
    setTheme({ ...preset, id: `custom-${Date.now()}`, name: `${preset.name} (Custom)` })
    setHasChanges(true)
  }

  // Color input component
  const ColorPicker = ({ label, value, onChange, description }: { 
    label: string; value: string; onChange: (v: string) => void; description?: string 
  }) => (
    <div className="flex items-center justify-between py-2 border-b border-soft">
      <div>
        <div className="text-sm font-medium">{label}</div>
        {description && <div className="text-xs text-muted">{description}</div>}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value.startsWith('rgba') ? '#000000' : value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded cursor-pointer border-2 border-soft"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-24 px-2 py-1 text-sm border rounded font-mono"
        />
      </div>
    </div>
  )

  // Preview paper background
  const previewPaperStyle = useMemo(() => getPaperBackground(theme, previewMode), [theme, previewMode, getPaperBackground])

  return (
    <div className="min-h-screen bg-default">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-soft px-6 py-4 bg-surface">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/profile?tab=preferences')}
              className="p-2 rounded-lg hover:bg-surface-alt transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-primary">
                Theme Builder
              </h1>
              <p className="text-sm text-secondary">
                Tune the live theme. Contrast is checked as you go.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPreviewMode(previewMode === 'light' ? 'dark' : 'light')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors"
              style={{ borderColor: 'var(--paper-margin)' }}
            >
              {previewMode === 'light' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span className="text-sm">{previewMode === 'light' ? 'Light' : 'Dark'}</span>
            </button>
            
            <Button variant="outline" onClick={resetTheme}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            
            <Button onClick={saveTheme} disabled={!hasChanges || isSaving}>
              {isSaving ? (
                <>
                  <Cloud className="h-4 w-4 mr-2 animate-pulse" />
                  Saving...
                </>
              ) : syncStatus === 'synced' ? (
                <>
                  <Cloud className="h-4 w-4 mr-2 text-ok" />
                  Saved ✓
                </>
              ) : syncStatus === 'error' ? (
                <>
                  <CloudOff className="h-4 w-4 mr-2 text-warn" />
                  Saved Locally
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Theme
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="inline-flex rounded-xl border border-soft bg-surface p-1">
          <button
            type="button"
            onClick={() => setLayer('studio')}
            className={`px-3 py-1.5 text-sm rounded-lg ${layer === 'studio' ? 'bg-brand-subtle text-brand font-semibold' : 'text-secondary'}`}
          >
            Any theme
          </button>
          <button
            type="button"
            onClick={() => setLayer('journal')}
            className={`px-3 py-1.5 text-sm rounded-lg ${layer === 'journal' ? 'bg-brand-subtle text-brand font-semibold' : 'text-secondary'}`}
          >
            Journal extras
          </button>
        </div>

        {layer === 'studio' && <ThemeStudio />}

        {layer === 'journal' && (
        <div className="grid grid-cols-12 gap-6">
          
          {/* Left Panel - Options */}
          <div className="col-span-5 space-y-4">
            {/* Section Tabs */}
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'presets', label: 'Presets', icon: Palette },
                { id: 'paper', label: 'Paper', icon: Image },
                { id: 'fonts', label: 'Fonts', icon: Type },
                { id: 'colors', label: 'Colors', icon: Droplets },
                { id: 'interactions', label: 'States', icon: MousePointer },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveSection(id as typeof activeSection)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeSection === id
                      ? 'bg-surface shadow-md'
                      : 'hover:bg-surface/50'
                  }`}
                  style={activeSection === id ? { color: 'var(--ink-sepia)' } : { color: 'var(--ink-faded)' }}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* Section Content */}
            <Card className="max-h-[calc(100vh-220px)] overflow-y-auto">
              <CardContent className="p-4 space-y-4">
                
                {/* Presets Section */}
                {activeSection === 'presets' && (
                  <div className="space-y-3">
                    <h3 className="font-medium text-sm" style={{ color: 'var(--ink-sepia)' }}>
                      Start with a preset
                    </h3>
                    <div className="grid grid-cols-1 gap-3">
                      {presetThemes.map(preset => {
                        const presetPaperStyle = getPaperBackground(preset, 'light')
                        return (
                          <button
                            key={preset.id}
                            onClick={() => loadPreset(preset)}
                            className="flex items-center gap-4 p-3 rounded-xl border-2 text-left transition-all hover:shadow-lg"
                            style={{ 
                              borderColor: theme.name.includes(preset.name) ? 'var(--bookmark-red)' : preset.light.borderLight,
                            }}
                          >
                            {/* Mini preview */}
                            <div 
                              className="w-16 h-20 rounded-lg border shadow-sm flex-shrink-0 overflow-hidden"
                              style={{ 
                                ...presetPaperStyle,
                                borderColor: preset.light.borderMedium,
                              }}
                            >
                              <div className="p-2">
                                <div 
                                  className="text-[8px] mb-1" 
                                  style={{ fontFamily: preset.fonts.handwritten, color: preset.light.inkSecondary }}
                                >
                                  Dear Diary
                                </div>
                                <div 
                                  className="text-[6px] leading-tight" 
                                  style={{ color: preset.light.inkFaded }}
                                >
                                  Today was a good day...
                                </div>
                              </div>
                            </div>
                            <div>
                              <div className="font-medium" style={{ color: preset.light.inkPrimary }}>
                                {preset.name}
                              </div>
                              <div className="text-sm" style={{ color: preset.light.inkFaded }}>
                                {preset.description}
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Paper Section */}
                {activeSection === 'paper' && (
                  <div className="space-y-6">
                    {/* Paper Color */}
                    <div>
                      <h3 className="font-medium text-sm mb-3" style={{ color: 'var(--ink-sepia)' }}>
                        📄 Paper Color
                      </h3>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {['solid', 'gradient'].map(type => (
                          <button
                            key={type}
                            onClick={() => updateTheme('paper', { ...theme.paper, type: type as 'solid' | 'gradient' })}
                            className={`px-3 py-2 rounded-lg text-sm capitalize transition-all ${
                              theme.paper.type === type ? 'bg-surface shadow-md ring-2 ring-warn' : 'hover:bg-surface/50 border'
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                      
                      {theme.paper.type === 'solid' && (
                        <ColorPicker
                          label="Paper Color"
                          value={theme.paper.solidColor}
                          onChange={(v) => {
                            updateTheme('paper', { ...theme.paper, solidColor: v })
                            updateColor(previewMode, 'paperPrimary', v)
                          }}
                        />
                      )}
                      
                      {theme.paper.type === 'gradient' && (
                        <div className="space-y-2">
                          <ColorPicker
                            label="Top Color"
                            value={theme.paper.gradientFrom}
                            onChange={(v) => updateTheme('paper', { ...theme.paper, gradientFrom: v })}
                          />
                          <ColorPicker
                            label="Bottom Color"
                            value={theme.paper.gradientTo}
                            onChange={(v) => updateTheme('paper', { ...theme.paper, gradientTo: v })}
                          />
                        </div>
                      )}
                    </div>

                    {/* Paper Texture */}
                    <div>
                      <h3 className="font-medium text-sm mb-3" style={{ color: 'var(--ink-sepia)' }}>
                        🎨 Paper Texture
                      </h3>
                      <div className="grid grid-cols-3 gap-2">
                        {paperTextures.map(texture => {
                          const texStyle = texture.getStyle(theme.paper.solidColor || '#FDF8F3', theme.light.inkFaded)
                          return (
                            <button
                              key={texture.id}
                              onClick={() => updateTheme('paper', { ...theme.paper, textureId: texture.id })}
                              className={`p-2 rounded-lg text-center transition-all ${
                                theme.paper.textureId === texture.id 
                                  ? 'ring-2 ring-warn shadow-md' 
                                  : 'hover:shadow-md border'
                              }`}
                            >
                              <div 
                                className="w-full h-12 rounded mb-2 border"
                                style={texStyle}
                              />
                              <div className="text-xs font-medium">{texture.name}</div>
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    {/* Line Pattern */}
                    <div>
                      <h3 className="font-medium text-sm mb-3" style={{ color: 'var(--ink-sepia)' }}>
                        📝 Line Pattern
                      </h3>
                      <div className="grid grid-cols-2 gap-2">
                        {linePatterns.map(pattern => {
                          const patStyle = pattern.getStyle(theme.light.inkFaded, 0.3)
                          return (
                            <button
                              key={pattern.id}
                              onClick={() => updateTheme('paper', { ...theme.paper, patternId: pattern.id })}
                              className={`p-2 rounded-lg text-center transition-all ${
                                theme.paper.patternId === pattern.id 
                                  ? 'ring-2 ring-warn shadow-md' 
                                  : 'hover:shadow-md border'
                              }`}
                            >
                              <div 
                                className="w-full h-16 rounded mb-2 border"
                                style={{
                                  backgroundColor: theme.paper.solidColor || '#FDF8F3',
                                  ...patStyle,
                                }}
                              />
                              <div className="text-xs font-medium">{pattern.name}</div>
                              <div className="text-app-micro text-muted">{pattern.description}</div>
                            </button>
                          )
                        })}
                      </div>
                      
                      {/* Pattern Opacity */}
                      {theme.paper.patternId !== 'none' && (
                        <div className="mt-4">
                          <label className="text-xs font-medium block mb-2">
                            Line Opacity: {Math.round(theme.paper.patternOpacity * 100)}%
                          </label>
                          <input
                            type="range"
                            min="0.05"
                            max="0.5"
                            step="0.05"
                            value={theme.paper.patternOpacity}
                            onChange={(e) => updateTheme('paper', { ...theme.paper, patternOpacity: parseFloat(e.target.value) })}
                            className="w-full"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Fonts Section */}
                {activeSection === 'fonts' && (
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm" style={{ color: 'var(--ink-sepia)' }}>
                      ✍️ Typography
                    </h3>
                    <p className="text-xs text-muted">
                      Fonts here style this theme's preview. The fonts the
                      site actually uses come from “Make it yours” on the
                      home screen.
                    </p>
                    
                    <div>
                      <label className="text-xs font-medium block mb-2">Handwritten Font</label>
                      <div className="space-y-2">
                        {DIARY_FONTS.map(font => (
                          <button
                            key={font.id}
                            onClick={() => updateTheme('fonts', { ...theme.fonts, handwritten: font.fontFamily })}
                            className={`w-full p-3 rounded-lg text-left transition-all ${
                              theme.fonts.handwritten === font.fontFamily 
                                ? 'bg-surface shadow-md ring-2 ring-warn'
                                : 'hover:bg-surface/50 border'
                            }`}
                          >
                            <div style={{ fontFamily: font.fontFamily }} className="text-xl mb-1">
                              Dear Diary...
                            </div>
                            <div className="text-xs text-muted">{font.name}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-medium block mb-2">Body Font</label>
                      <select
                        value={theme.fonts.body}
                        onChange={(e) => updateTheme('fonts', { ...theme.fonts, body: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border"
                      >
                        <option value="'Lora', serif">Lora (Serif)</option>
                        <option value="'Crimson Pro', serif">Crimson Pro</option>
                        <option value="'EB Garamond', serif">EB Garamond</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-medium block mb-2">UI Font</label>
                      <select
                        value={theme.fonts.ui}
                        onChange={(e) => updateTheme('fonts', { ...theme.fonts, ui: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border"
                      >
                        <option value="'Nunito', sans-serif">Nunito</option>
                        <option value="'Quicksand', sans-serif">Quicksand</option>
                        <option value="'Inter', sans-serif">Inter</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Colors Section */}
                {activeSection === 'colors' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-sm" style={{ color: 'var(--ink-sepia)' }}>
                        🎨 Colors ({previewMode})
                      </h3>
                      <button
                        onClick={() => setPreviewMode(previewMode === 'light' ? 'dark' : 'light')}
                        className="text-xs px-2 py-1 rounded bg-surface/50"
                      >
                        Edit {previewMode === 'light' ? 'Dark' : 'Light'}
                      </button>
                    </div>
                    
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-muted mt-2 mb-2">📄 Paper</div>
                      <ColorPicker
                        label="Primary"
                        value={theme[previewMode].paperPrimary}
                        onChange={(v) => updateColor(previewMode, 'paperPrimary', v)}
                      />
                      <ColorPicker
                        label="Secondary"
                        value={theme[previewMode].paperSecondary}
                        onChange={(v) => updateColor(previewMode, 'paperSecondary', v)}
                      />
                      
                      <div className="text-xs font-medium text-muted mt-4 mb-2">🖋️ Ink</div>
                      <ColorPicker
                        label="Primary Text"
                        value={theme[previewMode].inkPrimary}
                        onChange={(v) => updateColor(previewMode, 'inkPrimary', v)}
                      />
                      <ColorPicker
                        label="Secondary"
                        value={theme[previewMode].inkSecondary}
                        onChange={(v) => updateColor(previewMode, 'inkSecondary', v)}
                      />
                      <ColorPicker
                        label="Faded"
                        value={theme[previewMode].inkFaded}
                        onChange={(v) => updateColor(previewMode, 'inkFaded', v)}
                      />

                      <div className="text-xs font-medium text-muted mt-4 mb-2">✨ Accents</div>
                      <ColorPicker
                        label="Bookmark"
                        value={theme[previewMode].bookmark}
                        onChange={(v) => updateColor(previewMode, 'bookmark', v)}
                      />
                      <ColorPicker
                        label="Highlight"
                        value={theme[previewMode].highlight}
                        onChange={(v) => updateColor(previewMode, 'highlight', v)}
                      />
                      <ColorPicker
                        label="Primary Accent"
                        value={theme[previewMode].accentPrimary}
                        onChange={(v) => updateColor(previewMode, 'accentPrimary', v)}
                      />
                    </div>
                  </div>
                )}

                {/* Interactions Section */}
                {activeSection === 'interactions' && (
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm" style={{ color: 'var(--ink-sepia)' }}>
                      🖱️ Interactive ({previewMode})
                    </h3>
                    
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-muted mt-2 mb-2">🔗 Links</div>
                      <ColorPicker
                        label="Default"
                        value={theme[previewMode].linkDefault}
                        onChange={(v) => updateColor(previewMode, 'linkDefault', v)}
                      />
                      <ColorPicker
                        label="Hover"
                        value={theme[previewMode].linkHover}
                        onChange={(v) => updateColor(previewMode, 'linkHover', v)}
                      />

                      <div className="text-xs font-medium text-muted mt-4 mb-2">📦 Elements</div>
                      <ColorPicker
                        label="Focus Ring"
                        value={theme[previewMode].focusRing}
                        onChange={(v) => updateColor(previewMode, 'focusRing', v)}
                      />
                      <ColorPicker
                        label="Light Border"
                        value={theme[previewMode].borderLight}
                        onChange={(v) => updateColor(previewMode, 'borderLight', v)}
                      />
                      <ColorPicker
                        label="Medium Border"
                        value={theme[previewMode].borderMedium}
                        onChange={(v) => updateColor(previewMode, 'borderMedium', v)}
                      />
                    </div>
                  </div>
                )}

              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Live Preview */}
          <div className="col-span-7">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Eye className="h-5 w-5" />
                  Live Preview
                </CardTitle>
                <CardDescription>
                  Your journal with current settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Preview Container - Actual notebook look */}
                <div 
                  className="rounded-xl overflow-hidden shadow-xl"
                  style={{ 
                    boxShadow: `
                      0 4px 6px ${theme[previewMode].shadowColor},
                      0 10px 20px ${theme[previewMode].shadowColor},
                      inset 0 1px 0 rgba(255,255,255,0.1)
                    `,
                  }}
                >
                  {/* Notebook binding edge */}
                  <div 
                    className="h-3"
                    style={{ 
                      background: `linear-gradient(90deg, ${theme[previewMode].bookmark} 0%, ${theme[previewMode].bookmark}dd 50%, ${theme[previewMode].bookmark} 100%)`,
                    }}
                  />
                  
                  {/* Preview Content Area */}
                  <div 
                    className="min-h-[500px] p-8 relative"
                    style={previewPaperStyle}
                  >
                    {/* Date */}
                    <div 
                      className="text-sm mb-2"
                      style={{ fontFamily: theme.fonts.ui, color: theme[previewMode].inkFaded }}
                    >
                      {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                    
                    {/* Title */}
                    <h2 
                      className="text-4xl mb-4"
                      style={{ fontFamily: theme.fonts.handwritten, color: theme[previewMode].inkSecondary }}
                    >
                      Dear Diary...
                    </h2>

                    {/* Body Text */}
                    <p 
                      className="text-base leading-relaxed mb-4"
                      style={{ fontFamily: theme.fonts.body, color: theme[previewMode].inkPrimary }}
                    >
                      Today was a beautiful day. I woke up early and watched the sunrise 
                      through my window. The golden light streaming in reminded me why I 
                      love mornings so much.
                    </p>

                    <p 
                      className="text-base leading-relaxed mb-6"
                      style={{ fontFamily: theme.fonts.body, color: theme[previewMode].inkPrimary }}
                    >
                      I've been thinking about my goals lately. Maybe it's time to 
                      check out{' '}
                      <span 
                        style={{ 
                          color: theme[previewMode].linkDefault, 
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        this helpful resource
                      </span>
                      {' '}I found online.
                    </p>

                    {/* Highlighted text */}
                    <p 
                      className="text-base leading-relaxed mb-6"
                      style={{ fontFamily: theme.fonts.body, color: theme[previewMode].inkPrimary }}
                    >
                      The most important thing I learned:{' '}
                      <span 
                        className="px-1 rounded"
                        style={{ backgroundColor: theme[previewMode].highlight }}
                      >
                        be kind to yourself
                      </span>
                      . It's okay to take breaks.
                    </p>

                    {/* Card/Note */}
                    <div 
                      className="p-4 rounded-lg mb-6"
                      style={{ 
                        backgroundColor: theme[previewMode].paperSecondary,
                        borderLeft: `4px solid ${theme[previewMode].bookmark}`,
                        boxShadow: `2px 2px 8px ${theme[previewMode].shadowColor}`,
                      }}
                    >
                      <h3 
                        className="font-medium mb-2"
                        style={{ fontFamily: theme.fonts.ui, color: theme[previewMode].inkPrimary }}
                      >
                        📌 Note to Self
                      </h3>
                      <p 
                        className="text-sm"
                        style={{ color: theme[previewMode].inkFaded }}
                      >
                        Remember to call mom tomorrow!
                      </p>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 mb-6">
                      <button
                        className="px-4 py-2 rounded-lg text-on-brand font-medium shadow-md"
                        style={{ 
                          backgroundColor: theme[previewMode].accentPrimary,
                          fontFamily: theme.fonts.ui,
                        }}
                      >
                        Save Entry
                      </button>
                      <button
                        className="px-4 py-2 rounded-lg font-medium"
                        style={{ 
                          border: `2px solid ${theme[previewMode].borderMedium}`,
                          color: theme[previewMode].inkSecondary,
                          fontFamily: theme.fonts.ui,
                        }}
                      >
                        Add Photo
                      </button>
                    </div>

                    {/* Faded text / timestamp */}
                    <p 
                      className="text-sm italic"
                      style={{ color: theme[previewMode].inkFaded }}
                    >
                      Written at 10:42 PM · Feeling grateful 🌙
                    </p>
                  </div>
                </div>

                {/* Theme info */}
                <div className="mt-4 text-center text-sm text-muted">
                  <span className="font-medium">{theme.name}</span>
                  {hasChanges && <span className="text-warn ml-2">• Unsaved changes</span>}
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
        )}
      </div>
    </div>
  )
}

export default ThemeBuilder
